import { useCallback } from 'react';
import type { Theme } from '@jira/platform__common-constants/src/theme-preference.tsx';
// eslint-disable-next-line jira/import-whitelist
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useResource, createResource, type RouteResource } from '@atlassian/react-resource-router';
import {
	THEME_PREFERENCES_ANALYTICS_SUBJECT,
	THEME_PREFERENCES_ANALYTICS_ACTION,
} from './themeColorModes.constants.tsx';
import { setThemePreference as setThemePreferenceOld } from './setThemePreference.tsx';
import type { ThemePreferenceResource } from './ThemePreferenceResource.types.tsx';
import { useThemePreference as useThemePreferenceNew } from './ResourceErai1615OldUseThemePreference.tsx';

export const themePreferenceResource: RouteResource<ThemePreferenceResource> =
	createResource<ThemePreferenceResource>({
		type: 'RESOURCE_THEME_PREFERENCE',
		getKey: () => 'state',
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-router-resources-theme-preference", jiraSpaEntry: "async-router-resources-theme-preference" */ './getData.tsx'
			),
		maxAge: 5 * 60 * 1000,
		isCritical: true,
	});

// TODO (DST): Should be deleted along with shared-user-preferences cleanup
/**
 * Original implementation of useThemePreference (pre shared-user-preferences feature)
 */
const useThemePreferenceOld = () => {
	const { data, update: updateThemePreferenceResource } = useResource(themePreferenceResource);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireThemeAnalytics = useCallback(
		(current?: Theme, next?: Theme) => {
			const OStheme =
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window?.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
					? 'dark'
					: 'light';
			const analyticsEvent = createAnalyticsEvent({
				action: THEME_PREFERENCES_ANALYTICS_ACTION,
				actionSubject: THEME_PREFERENCES_ANALYTICS_SUBJECT,
			});
			fireUIAnalytics(analyticsEvent, {
				newPreference: next,
				oldPreference: current,
				OStheme,
			});
		},
		[createAnalyticsEvent],
	);

	const setTheme = useCallback(
		async (theme: Theme): Promise<void> => {
			updateThemePreferenceResource(() => ({ theme }));
			if (data?.theme !== theme) {
				fireThemeAnalytics(data?.theme, theme);
				await setThemePreferenceOld(theme);
			}
		},
		[data?.theme, updateThemePreferenceResource, fireThemeAnalytics],
	);

	return {
		theme: data?.theme,
		setTheme,
	};
};

const useThemePreferenceCombined = functionUnionWithCondition(
	() => fg('shared-user-preferences'),
	() => useThemePreferenceNew(themePreferenceResource),
	useThemePreferenceOld,
);

/**
 * Hook to get and set theme preference.
 * Delegates to either the new implementation (with shared-user-preferences)
 * or the old implementation based on the feature flag.
 */
export const useThemePreference = (): {
	theme: Theme | undefined;
	setTheme: (theme: Theme) => Promise<void>;
} => {
	return useThemePreferenceCombined();
};
